import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FieldValues, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { MdClose } from 'react-icons/md';
import { getUser } from '../../../../api/client';
import { selectCompositeEditing, selectCompositeEditingParent, setCompositeEditing, upsertComposite } from './edit-composite-slice';
import { recalculatePortfolio } from '../../portfolios-slice';
import { setSelectedComposite } from '../portfolio-allocation/collapsible-composite-slice';

/**
 * Form to add and edit a category
 */
const EditComposite: React.FC = () => {
  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const portfolioCompositeEditing = useAppSelector(selectCompositeEditing);
  const portfolioCompositeEditingParent = useAppSelector(selectCompositeEditingParent);
  const { register, handleSubmit, formState: { errors } } = useForm({
    values: {
      categoryName: portfolioCompositeEditing?.category.name,
      categoryDescription: portfolioCompositeEditing?.category.description,
      weight: portfolioCompositeEditing?.weight
    }
  });
  const isNewComposite = portfolioCompositeEditing?.id ? false : true;

  const useOnSubmit = (data: FieldValues) => {
    const execute = async () => {
      const accessToken = await getAccessTokenSilently();
      const user = await getUser(accessToken);
      if (user && portfolioCompositeEditing) {

        // If the category is public, change the id to undefined so it can be created for this user in case of a category name or description change
        if (!portfolioCompositeEditing.category.userId) {
          portfolioCompositeEditing.category.id = undefined;
        }

        portfolioCompositeEditing.category.name = data['categoryName'];
        portfolioCompositeEditing.category.description = data['categoryDescription'];
        portfolioCompositeEditing.weight = Number.parseInt(data['weight']);

        dispatch(upsertComposite({ accessToken, user, composite: portfolioCompositeEditing }));
        dispatch(setSelectedComposite(undefined));
        dispatch(recalculatePortfolio({ portfolioId: portfolioCompositeEditing.portfolioId }));

      }
    }
    execute();
  };

  const closeEditHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    dispatch(setCompositeEditing({ composite: undefined, parent: undefined }));
  }

  return (
    <div className='flex flex-col w-full p-4 z-40'>
      <div className='flex justify-end'>
        <div onMouseDown={closeEditHandler} className='cursor-pointer'>
          <MdClose size={20} />
        </div>
      </div>
      <form onSubmit={handleSubmit(useOnSubmit)} className='w-full flex flex-col text-slate-700'>
        <div className='flex flex-col w-full mb-6 border-b pb-4 text-lg text-center'>
          <label>{isNewComposite ? 'Criar Subcategoria' : 'Editar Categoria'}</label>
          <label>{isNewComposite ? `(na categoria "${portfolioCompositeEditingParent?.category.name}")` : ''}</label>
        </div>

        <div className='flex flex-col mb-4'>
          <label className='ml-1'>Nome</label>
          <input type="text" placeholder="ex: RV Internacional" className='text-sm font-medium px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 shadow-md shadow-neutral-400/40'
            {...register("categoryName", { required: { value: true, message: 'O nome é obrigatório' } })}
          />
          <ErrorMessage errors={errors} name="categoryName" render={({ message }) => <div className='text-red-500 text-sm font-semibold'>{message}</div>} />
        </div>

        <div className='flex flex-col mb-4'>
          <label className='ml-1'>Descrição</label>
          <textarea placeholder="ex: Ações/ETF de fora do Brasil." className='text-sm font-medium px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 shadow-md shadow-neutral-400/40 h-24'
            {...register("categoryDescription", { required: false })}
          />
        </div>

        <div className='flex flex-col mb-4'>
          <label className='ml-1'>Peso</label>
          <input type="number" placeholder="ex: 10" className='text-sm font-medium px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 shadow-md shadow-neutral-400/40'
            {...register("weight", { required: true, min: { value: 0, message: 'O peso não pode ser negativo' } })}
          />
          <ErrorMessage errors={errors} name="weight" render={({ message }) => <div className='text-red-500 text-sm font-semibold'>{message}</div>} />
        </div>

        <div className='flex justify-center items-center mt-4'>
          <input type="submit" value="Enviar" className='cursor-pointer px-4 py-2 text-slate-200 rounded-full bg-slate-800 hover:opacity-85 hover:shadow-lg hover:border-slate-600 hover:border-1' />
        </div>
      </form >
    </div>
  );
}

export default EditComposite;